import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { asset } from '../lib/assets'
import { env } from '../config/env'
import { getEnvSessionDefaults, SESSION_EXPIRED_MESSAGE, type AuthSession } from './session'
import { isGoogleLoginConfigured, loginWithGoogle, loginWithPassword, preloadGoogleLogin } from './login'
import { RowSkeleton } from '../components/Skeleton'
import { getProfile, listLocations, listProviders, type LocationOption, type ProfileInfo, type ProviderOption } from './scope'
import { ScopeCombobox } from './ScopeCombobox'
import { ApiError } from '../data/errors'

type Step = 'login' | 'scope'

export function AuthFlow({ onComplete, initialSession, onCancel, sessionExpired = false }: {
  onComplete: (session: AuthSession) => void
  initialSession?: AuthSession
  onCancel?: () => void
  sessionExpired?: boolean
}) {
  const defaults = useMemo(() => getEnvSessionDefaults(), [])
  const [step, setStep] = useState<Step>(initialSession ? 'scope' : 'login')
  const [email, setEmail] = useState(initialSession?.email ?? '')
  const [password, setPassword] = useState('')
  const [token, setToken] = useState(initialSession?.token ?? '')
  const [providerId, setProviderId] = useState(initialSession?.providerId ?? defaults.providerId ?? '')
  const [locationId, setLocationId] = useState(initialSession?.locationId ?? defaults.locationId ?? '')
  const [error, setError] = useState<string | null>(sessionExpired ? SESSION_EXPIRED_MESSAGE : null)
  const [loggingIn, setLoggingIn] = useState(false)
  const [providers, setProviders] = useState<ProviderOption[]>([])
  const [locations, setLocations] = useState<LocationOption[]>([])
  const [profile, setProfile] = useState<ProfileInfo | null>(
    initialSession ? { email: initialSession.email, name: initialSession.userName } : null,
  )
  const [loadingProviders, setLoadingProviders] = useState(false)
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [loadingProfile, setLoadingProfile] = useState(!!initialSession?.token)
  const providersVersionRef = useRef(0)
  const locationsVersionRef = useRef(0)

  const canLogin = email.trim() !== '' && password !== '' && !loggingIn
  const canLoginWithGoogle = isGoogleLoginConfigured() && !loggingIn
  const selectedProvider = providers.find((provider) => provider.id === providerId)
  const selectedLocation = locations.find((location) => location.id === locationId)
  const canSelectScope = !!selectedProvider && !!selectedLocation && !loadingProviders && !loadingLocations && !loadingProfile

  const handleScopeError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 401) {
      setToken('')
      setStep('login')
    }
    setError(err instanceof Error ? err.message : fallback)
  }

  const loadProviders = async (accessToken: string, preferredProviderId?: string, preferredLocationId?: string) => {
    const myVersion = ++providersVersionRef.current
    ++locationsVersionRef.current
    setLoadingProviders(true)
    setProviders([])
    setLocations([])
    setProviderId('')
    setLocationId('')
    setError(null)
    try {
      const nextProviders = await listProviders(accessToken)
      if (myVersion !== providersVersionRef.current) {return}
      setProviders(nextProviders)
      const providerToSelect = nextProviders.some((provider) => provider.id === preferredProviderId)
        ? preferredProviderId
        : nextProviders.length === 1 ? nextProviders[0].id : ''
      if (providerToSelect) {
        await selectProvider(accessToken, providerToSelect, preferredLocationId)
      }
      if (myVersion !== providersVersionRef.current) {return}
      if (nextProviders.length === 0) {setError('Nenhum provider disponível para este usuário.')}
    } catch (err) {
      if (myVersion !== providersVersionRef.current) {return}
      handleScopeError(err, 'Não foi possível carregar providers.')
    } finally {
      if (myVersion === providersVersionRef.current) {setLoadingProviders(false)}
    }
  }

  const selectProvider = async (accessToken: string, nextProviderId: string, preferredLocationId?: string) => {
    const myVersion = ++locationsVersionRef.current
    setProviderId(nextProviderId)
    setLocationId('')
    setLocations([])
    setError(null)
    if (!nextProviderId) {return}
    setLoadingLocations(true)
    try {
      const nextLocations = await listLocations(accessToken, nextProviderId)
      if (myVersion !== locationsVersionRef.current) {return}
      setLocations(nextLocations)
      const locationToSelect = nextLocations.some((location) => location.id === preferredLocationId)
        ? preferredLocationId
        : nextLocations.length === 1 ? nextLocations[0].id : ''
      if (locationToSelect) {setLocationId(locationToSelect)}
      if (nextLocations.length === 0) {setError('Nenhuma unidade disponível para este provider.')}
    } catch (err) {
      if (myVersion !== locationsVersionRef.current) {return}
      handleScopeError(err, 'Não foi possível carregar unidades.')
    } finally {
      if (myVersion === locationsVersionRef.current) {setLoadingLocations(false)}
    }
  }

  const continueWithToken = async (accessToken: string, fallbackEmail?: string) => {
    const nextProfile = await getProfile(accessToken).catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 401) {throw err}
      return { email: fallbackEmail, name: fallbackEmail }
    })
    setToken(accessToken)
    setProfile(nextProfile)
    setPassword('')
    setStep('scope')
    await loadProviders(accessToken)
  }

  useEffect(() => {
    if (!initialSession?.token) {return}
    // refaz o fetch do profile aqui pois o providerRoles não é persistido na AuthSession —
    // só o role já resolvido para o provider selecionado é.
    setLoadingProfile(true)
    void getProfile(initialSession.token)
      .then(setProfile)
      .catch(() => {})
      .finally(() => setLoadingProfile(false))
    void loadProviders(initialSession.token, initialSession.providerId, initialSession.locationId)
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [])

  useEffect(() => { preloadGoogleLogin() }, [])

  const submitLogin = async (e: FormEvent) => {
    e.preventDefault()
    if (!canLogin) {
      setError('Informe e-mail e senha para continuar.')
      return
    }
    try {
      setLoggingIn(true)
      setError(null)
      const accessToken = await loginWithPassword(email.trim(), password)
      await continueWithToken(accessToken, email.trim())
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível entrar.'
      setError(
        message.includes('401') || message.includes('400') || message.includes('invalid_clientId') || message.includes('not registered')
          ? 'Não foi possível autenticar com este e-mail e senha.'
          : message,
      )
    } finally {
      setLoggingIn(false)
    }
  }

  const submitGoogleLogin = async () => {
    if (!canLoginWithGoogle) {
      setError('Login com Google não configurado neste ambiente.')
      return
    }
    try {
      setLoggingIn(true)
      setError(null)
      const accessToken = await loginWithGoogle()
      await continueWithToken(accessToken)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível entrar com Google.'
      setError(message)
    } finally {
      setLoggingIn(false)
    }
  }

  const submitScope = (e: FormEvent) => {
    e.preventDefault()
    if (!token) {
      setStep('login')
      setError('Entre novamente para gerar um token válido.')
      return
    }
    if (!canSelectScope) {
      setError('Escolha uma empresa e uma unidade antes de entrar.')
      return
    }
    if (!selectedProvider || !selectedLocation) {return}
    onComplete({
      token,
      email: profile?.email || email.trim() || undefined,
      userName: profile?.name || profile?.email || email.trim() || undefined,
      role: profile?.providerRoles?.[selectedProvider.id],
      providerId: selectedProvider.id,
      locationId: selectedLocation.id,
      providerName: selectedProvider.name,
      locationName: selectedLocation.name,
      locationAddress: selectedLocation.address,
    })
  }

  return (
    <main className="fz-auth-shell">
      <section className="fz-auth-brand">
        <img src={asset('favicon-fi-480.png')} alt="" className="fz-auth-brand__mark" />
        <div>
          <div className="fz-auth-brand__eyebrow">Filazero - Jornada</div>
          <h1 className="fz-auth-brand__title">Acesse sua operação</h1>
          <p className="fz-auth-brand__copy">Entre com seu usuário e escolha a fila para abrir o painel.</p>
        </div>
      </section>

      <section className="fz-auth-panel" aria-label={step === 'login' ? 'Login' : 'Selecionar operação'}>
        <div className="fz-auth-steps" aria-hidden="true">
          <span className={'fz-auth-step ' + (step === 'login' ? 'is-active' : 'is-done')}>1</span>
          <span className="fz-auth-steps__line" />
          <span className={'fz-auth-step ' + (step === 'scope' ? 'is-active' : '')}>2</span>
        </div>

        {step === 'login' ? (
          <form className="fz-auth-form" onSubmit={submitLogin}>
            <div>
              <h2 className="fz-auth-title">Entrar</h2>
              <p className="fz-auth-sub">Acesse sua conta para escolher a clínica e iniciar os atendimentos.</p>
            </div>

            <label className="fz-auth-field">
              <span>E-mail</span>
              <input autoFocus value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="exemplo@email.com" autoComplete="email" />
            </label>

            <label className="fz-auth-field">
              <span>Senha</span>
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Sua senha" autoComplete="current-password" />
            </label>
            <p className="fz-auth-footer">
              <a href={env.forgotPasswordUrl} target="_blank" rel="noreferrer">Esqueceu a senha?</a>
            </p>

            {error && <div className="fz-auth-error" role="alert">{error}</div>}

            <button className="fz-btn fz-btn--primary fz-btn--lg fz-btn--block" type="submit" disabled={!canLogin}>
              {loggingIn ? <><span className="fz-spin" /> Entrando…</> : 'Continuar'}
            </button>

            <div className="fz-auth-divider"><span>ou</span></div>

            <button className="fz-btn fz-btn--neutral fz-btn--lg fz-btn--block fz-auth-google" type="button" onClick={submitGoogleLogin} disabled={!canLoginWithGoogle}>
              <svg className="fz-auth-google__icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                <path d="M1 1h22v22H1z" fill="none" />
              </svg>
              Entrar com Google
            </button>

          </form>
        ) : (
          <form className="fz-auth-form" onSubmit={submitScope}>
            <div>
              <h2 className="fz-auth-title">Escolher operação</h2>
              <p className="fz-auth-sub">Escolha uma empresa liberada para seu usuário e depois a unidade de atendimento.</p>
            </div>

            <ScopeCombobox
              label="Empresa"
              autoFocus
              value={providerId}
              onChange={(id) => { setError(null); void selectProvider(token, id) }}
              disabled={loadingProviders || providers.length === 0}
              placeholder={loadingProviders ? 'Carregando empresas…' : 'Selecione uma empresa'}
              options={providers.map((provider) => ({ id: provider.id, label: `${provider.name} · #${provider.id}` }))}
            />

            {(loadingProviders || (providerId && loadingLocations)) && (
              <RowSkeleton count={2} ariaLabel="Carregando opções" />
            )}

            <ScopeCombobox
              label="Unidade"
              value={locationId}
              onChange={setLocationId}
              disabled={!providerId || loadingLocations || locations.length === 0}
              placeholder={loadingLocations ? 'Carregando unidades…' : providerId ? 'Selecione uma unidade' : 'Escolha uma empresa primeiro'}
              options={locations.map((location) => ({ id: location.id, label: `${location.address ? `${location.name} — ${location.address}` : location.name} · #${location.id}` }))}
            />

            {error && <div className="fz-auth-error" role="alert">{error}</div>}

            <div className="fz-auth-actions">
              <button className="fz-btn fz-btn--neutral fz-btn--lg" type="button" onClick={() => {
                setError(null)
                if (onCancel) {onCancel()}
                else {setStep('login')}
              }}>
                Voltar
              </button>
              <button className="fz-btn fz-btn--primary fz-btn--lg" type="submit" disabled={!canSelectScope}>
                Entrar no dashboard
              </button>
            </div>
          </form>
        )}
      </section>
    </main>
  )
}
